<template>
  <header class="panel-summary" :class="{ 'panel-summary--stale': stale }">
    <!--
      A grade das faixas mora aqui dentro, e não no <header>: o cabeçalho é o
      container que ela consulta, e a consulta só alcança os descendentes.
    -->
    <div class="panel-summary__layout">
      <div class="panel-summary__brand">
        <img
          class="panel-summary__logo"
          :src="logoSrc"
          alt="Sigma ECM"
        >
        <div class="panel-summary__identity">
          <h1 class="panel-summary__title">{{ $t('communication-status-panel.title') }}</h1>
          <p class="panel-summary__scope">{{ scopeLabel }}</p>
        </div>
      </div>

      <ul class="panel-summary__counters">
        <li
          v-for="counter in counters"
          :key="counter.state"
          class="panel-summary__counter"
          :class="[
            `panel-summary__counter--${counter.state}`,
            { 'panel-summary__counter--empty': !counter.value },
          ]"
        >
          <s-icon class="panel-summary__counter-icon" :name="counter.icon" />
          <span class="panel-summary__counter-value">{{ counter.value }}</span>
          <span class="panel-summary__counter-label">{{ counter.label }}</span>
        </li>
      </ul>

      <div class="panel-summary__freshness">
        <p class="panel-summary__age">{{ ageLabel }}</p>
        <!--
          Rolagem Automática mora aqui, e não na barra de controles: não recorta o
          que se vê, muda como a tela se comporta. Some no Radial, que não rola.
        -->
        <button
          v-if="autoScrollAvailable"
          type="button"
          class="panel-summary__auto-scroll"
          :class="{ 'panel-summary__auto-scroll--on': autoScroll }"
          :aria-pressed="String(autoScroll)"
          @click="$emit('update:auto-scroll', !autoScroll)"
        >
          <s-icon name="auto-scroll" />
          <span>{{ $t('communication-status-panel.auto-scroll') }}</span>
        </button>
        <!--
          Selo da página, logo abaixo do botão. Só visual: anunciar "página 2 de 4"
          a cada 30 segundos num leitor de tela seria ruído.
        -->
        <div
          v-if="autoScrollAvailable && autoScrollStatus.running"
          class="panel-summary__auto-scroll-status"
          :class="{ 'panel-summary__auto-scroll-status--paused': autoScrollStatus.paused }"
          aria-hidden="true"
        >
          <span class="panel-summary__auto-scroll-page">{{ autoScrollLabel }}</span>
          <span class="panel-summary__auto-scroll-track">
            <span
              v-if="!autoScrollStatus.paused"
              :key="autoScrollStatus.cycleKey"
              class="panel-summary__auto-scroll-progress"
              :style="{ animationDuration: `${autoScrollStatus.dwellMs}ms` }"
            />
          </span>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
import { assetStates, assetStatesBySeverity } from '@/domain/enums/assetState';
import { describeAge } from '@/domain/services/generalPanel/communicationStatusPanel/dataAge';

/**
 * Ícone de cada Estado do Ativo no Placar. Lido a 3 metros, a forma chega antes
 * do número: o operador identifica a coluna sem precisar ler o rótulo.
 */
const STATE_ICONS = {
  [assetStates.ATTENTION]: 'exclamation-triangle',
  [assetStates.NORMAL]: 'check-circle',
  [assetStates.OUT_OF_SERVICE]: 'power-off',
};

export default {
  name: 'PanelSummary',
  props: {
    summary: {
      type: Object,
      required: true,
    },
    ageInMs: {
      type: Number,
      default: null,
    },
    stale: {
      type: Boolean,
      default: false,
    },
    /** Rolagem Automática ligada. Quem guarda o estado é a página, na URL. */
    autoScroll: {
      type: Boolean,
      default: false,
    },
    /** Só há o que rolar na Grade: no Radial o botão não aparece. */
    autoScrollAvailable: {
      type: Boolean,
      default: false,
    },
    /**
     * Estado emitido pela Rolagem Automática: `{ running, paused, current,
     * total, cycleKey, dwellMs }`. O selo só aparece com ela rodando de fato.
     */
    autoScrollStatus: {
      type: Object,
      default: () => ({ running: false }),
    },
  },
  computed: {
    /**
     * O logo tem duas tintas: branca para fundo escuro, verde da marca para
     * fundo claro. Sem a troca, o tema claro exibiria branco sobre branco.
     */
    logoSrc() {
      return this.$store.getters.theme === 'light'
        ? '/static/imgs/sigma-ecm-logo-green.svg'
        : '/static/imgs/sigma-ecm-logo.svg';
    },
    counters() {
      return assetStatesBySeverity.map(state => ({
        state,
        icon: STATE_ICONS[state],
        value: (this.summary.byState || {})[state] || 0,
        label: this.$t(`communication-status-panel.state.${state}`),
      }));
    },
    scopeLabel() {
      return this.$t('communication-status-panel.scope', {
        installations: this.summary.installationCount || 0,
        assets: this.summary.assetCount || 0,
        sensors: this.summary.sensorCount || 0,
      });
    },
    /**
     * Rótulo permanente de confiança. Enquanto ele avança normalmente, o operador
     * sabe que a tela está viva.
     */
    ageLabel() {
      if (this.ageInMs === null) {
        return this.$t('communication-status-panel.never-updated');
      }

      const { unit, value } = describeAge(this.ageInMs);
      const age = this.$t(`communication-status-panel.age.${unit}`, { count: value });

      return this.$t('communication-status-panel.updated-ago', { age });
    },
    autoScrollLabel() {
      if (this.autoScrollStatus.paused) {
        return this.$t('communication-status-panel.auto-scroll-paused');
      }

      return this.$t('communication-status-panel.auto-scroll-page', {
        current: this.autoScrollStatus.current,
        total: this.autoScrollStatus.total,
      });
    },
  },
};
</script>

<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"
@import "src/styles/components/communication-status-panel-fluid"

// Os limites das faixas medem o conteúdo do próprio cabeçalho, não a janela: o
// menu lateral e o submenu tiram largura dele sem a janela mudar. Calibrados
// com o título em pt-BR ("PAINEL DE STATUS DE COMUNICAÇÃO", o mais comprido).
// Faixa única com o título em duas linhas: marca (~36rem), Placar (32rem) e
// Idade do Dado (~12rem) lado a lado.
$single-band-min: 82rem
// Título numa linha só: a marca passa a precisar de ~56rem.
$single-line-title-min: 106rem

// O cabeçalho é a régua da própria distribuição. Ver Placar do Painel no
// CONTEXT.md: a faixa cede em degraus, nunca com um bloco sozinho numa linha.
.panel-summary
  background: var(--panel-surface)
  border-bottom: 1px solid var(--panel-border)
  container-name: panel-summary
  container-type: inline-size
  padding: 0.7rem 1.2rem

  // Padrão: duas faixas — marca e Idade do Dado em cima, Placar embaixo na
  // largura toda. É também o que vê o navegador que não sabe medir o
  // cabeçalho (TV com navegador embarcado antigo): mais alto que o ideal, mas
  // nunca desalinhado. A faixa única é ligada lá embaixo, por @container.
  &__layout
    align-items: center
    column-gap: 1.2rem
    display: grid
    grid-template-areas: "brand freshness" "counters counters"
    grid-template-columns: minmax(0, 1fr) auto
    row-gap: 0.7rem

  &__brand
    align-items: center
    display: flex
    gap: 0.85rem
    grid-area: brand
    min-width: 0

  // A variante é escolhida em `logoSrc`, conforme o tema do perfil.
  &__logo
    flex: none
    height: 2.8rem
    width: auto

  &__identity
    border-left: 1px solid var(--panel-border)
    min-width: 0
    padding-left: 0.85rem

  // Lido a três metros, na TV: o título se sustenta sozinho, sem precisar de
  // alguém perto da tela para dizer que painel é aquele. Quando quebra, as
  // linhas saem de tamanho parecido em vez de uma palavra sozinha embaixo.
  &__title
    color: var(--panel-text)
    font-size: 2rem
    line-height: 1.1
    font-weight: 700
    letter-spacing: 0.04em
    margin: 0
    text-transform: uppercase
    text-wrap: balance

  &__scope
    color: var(--panel-text-muted)
    font-size: 0.95rem
    margin: 0

  // Três colunas iguais: a largura do card não conta história nenhuma, só o
  // número conta. Colunas iguais mantêm a comparação entre estados honesta —
  // card do tamanho do próprio conteúdo faria "Fora de operação" ficar duas
  // vezes maior que "Normal", sugerindo peso onde só há rótulo mais comprido.
  &__counters
    display: grid
    gap: 0.7rem
    grid-area: counters
    grid-auto-columns: minmax(0, 1fr)
    grid-auto-flow: column
    list-style: none
    margin: 0
    padding: 0

  // Ícone numa coluna própria e conteúdo encostado à esquerda: com os cards de
  // mesma largura, ícones, números e rótulos caem na mesma vertical nos três.
  // Centralizar o conteúdo dentro de cada card desalinharia tudo, porque "2" e
  // "64" ocupam larguras diferentes. O formato é o mesmo em todas as faixas.
  &__counter
    align-content: center
    border: 1px solid var(--panel-border)
    border-radius: 6px
    column-gap: 0.75rem
    // Cada card é a régua do próprio texto: ícone, número e rótulo em `cqw`.
    container-type: inline-size
    display: grid
    grid-template-areas: "icon value" "icon label"
    grid-template-columns: auto 1fr
    justify-items: start
    padding: 0.5rem 1rem
    row-gap: 0.2rem

  &__counter-icon
    align-self: center
    +panel-fluid(font-size, 1.6rem, 16cqw, 2.8rem)
    grid-area: icon
    line-height: 1
    opacity: 0.9

  &__counter-value
    align-self: end
    +panel-fluid(font-size, 2rem, 22cqw, 3.6rem)
    font-variant-numeric: tabular-nums
    font-weight: 700
    grid-area: value
    line-height: 1

  // O rótulo é lido a três metros junto com o número: em corpo miúdo ele vira
  // textura e só o valor sobra, sem dizer de que estado é.
  &__counter-label
    align-self: start
    color: var(--panel-text-muted)
    // 8cqw deixa "Fora de operação" numa linha ao lado do ícone em qualquer
    // largura. A quebra só acontece se o card ficar no mínimo com o piso de
    // 1rem — melhor quebrar que vazar do card.
    +panel-fluid(font-size, 1rem, 8cqw, 1.6rem)
    font-weight: 600
    grid-area: label
    letter-spacing: 0.03em
    line-height: 1.1

  // A tinta é o destaque. Zerado, o card perde a cor e vira fundo: de longe só
  // o que existe puxa o olho.
  &__counter--attention
    background: var(--panel-card-attention)
    border-color: var(--panel-edge-attention)
    color: var(--panel-attention)
  &__counter--normal
    background: var(--panel-card-normal)
    border-color: var(--panel-edge-normal)
    color: var(--panel-normal)
  &__counter--out-of-service
    background: var(--panel-card-out-of-service)
    border-color: var(--panel-edge-out-of-service)
    color: var(--panel-out-of-service)

  &__counter--empty
    background: var(--panel-surface-raised)
    border-color: var(--panel-border)
    color: var(--panel-text-muted)

  // Idade do Dado em cima, Rolagem Automática embaixo, os dois encostados à
  // direita: o que diz respeito à tela, e não à frota, fica junto.
  &__freshness
    align-items: flex-end
    display: flex
    flex-direction: column
    gap: 0.4rem
    grid-area: freshness
    justify-self: end
    text-align: right

  &__auto-scroll
    align-items: center
    background: transparent
    border: 1px solid var(--panel-border)
    border-radius: 999px
    color: var(--panel-text-muted)
    cursor: pointer
    display: inline-flex
    font-size: 0.8rem
    gap: 0.4rem
    line-height: 1
    padding: 0.3rem 0.75rem
    transition: background-color 160ms ease-out, color 160ms ease-out

    &:hover
      background: var(--panel-chip-hover)
      color: var(--panel-text)

    // Ligada, só o texto e o ícone ganham a cor de destaque, sem preenchimento:
    // é um estado da tela, não um alarme, e não pode competir com os cards do
    // Placar. O selo de página logo abaixo já confirma que está rodando.
    &--on
      color: var(--panel-match)

      &:hover
        color: var(--panel-match)

  // Selo da página, logo abaixo do botão: onde se está e quanto falta para a
  // próxima. Pausada, a barra some e o texto muda de cor.
  &__auto-scroll-status
    align-items: center
    color: var(--panel-text-muted)
    display: flex
    font-size: 0.8rem
    font-variant-numeric: tabular-nums
    gap: 0.5rem

    &--paused
      color: var(--panel-stale)

  &__auto-scroll-track
    background: var(--panel-donut-track)
    border-radius: 999px
    height: 3px
    overflow: hidden
    width: 3.5rem

  // Só `transform` anima: fica no compositor, sem repaint durante os 30s.
  &__auto-scroll-progress
    animation-fill-mode: forwards
    animation-name: panel-summary-auto-scroll-progress
    animation-timing-function: linear
    background: var(--panel-match)
    display: block
    height: 100%
    transform: scaleX(0)
    transform-origin: left center

  // A Idade do Dado é o sinal de que a tela está viva: precisa ser lida de
  // longe tanto quanto os contadores. Numa linha só — quebrada, "56s" ficaria
  // sozinho embaixo e a coluna cresceria à toa.
  &__age
    color: var(--panel-text-muted)
    font-size: 1.3rem
    font-weight: 600
    font-variant-numeric: tabular-nums
    margin: 0
    white-space: nowrap

  &--stale
    background: var(--panel-stale-wash)
    border-bottom-color: var(--panel-stale)

    .panel-summary__age
      color: var(--panel-stale)
      font-weight: 700

  // Faixa única: marca à esquerda, Idade do Dado à direita e o Placar entre as
  // duas. O título quebra em duas linhas: é ele que cede primeiro, porque é
  // lido uma vez, enquanto cards e Idade do Dado são lidos o tempo todo.
  //
  // Seletores `& &__x` (descendentes da raiz) de propósito: o CSS scoped do Vue
  // 2 só carimba `[data-v-…]` dentro de @media e @supports. Dentro de
  // @container a regra sairia sem o carimbo, menos específica que a regra base
  // carimbada, e perderia sempre. Com a raiz na frente a especificidade empata e
  // vale a ordem — estas vêm depois.
  @container panel-summary (min-width: #{$single-band-min})
    & &__layout
      grid-template-areas: "brand counters freshness"
      grid-template-columns: auto minmax(32rem, 1fr) auto

    // ~12 caracteres do título por linha: "PAINEL DE STATUS" / "DE COMUNICAÇÃO".
    & &__identity
      max-width: 24rem

    // O Placar toma o espaço livre até o teto, e as margens centralizam o resto.
    & &__counters
      justify-self: center
      max-width: 64rem
      width: 100%

  // Com espaço de sobra, o título volta a ser uma linha só.
  @container panel-summary (min-width: #{$single-line-title-min})
    & &__identity
      max-width: none

  // Celular: o Placar divide a largura em três, e tudo encolhe junto para caber
  // sem rolagem lateral. As proporções entre título, número e rótulo se mantêm.
  +media-max-sm()
    padding: 0.6rem 0.8rem

    &__layout
      column-gap: 0.6rem
      row-gap: 0.6rem

    &__logo
      height: 2rem

    &__identity
      padding-left: 0.6rem

    &__title
      font-size: 1.3rem

    &__scope
      font-size: 0.78rem

    &__age
      font-size: 1rem

    // No celular a página inteira rola, sem container próprio: o botão não
    // teria o que rolar.
    &__auto-scroll,
    &__auto-scroll-status
      display: none

    &__counters
      gap: 0.4rem

    &__counter
      column-gap: 0.4rem
      padding: 0.4rem 0.5rem

    &__counter-icon
      font-size: 1.1rem

    &__counter-value
      font-size: 1.4rem

    // "Fora de operação" não cabe numa linha num terço de celular: quebra em
    // vez de estourar o card.
    &__counter-label
      font-size: 0.72rem
      line-height: 1.15
      white-space: normal

@keyframes panel-summary-auto-scroll-progress
  from
    transform: scaleX(0)
  to
    transform: scaleX(1)
</style>
